import React, { useState, useEffect, useCallback } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { 
  Shield, 
  Users, 
  Building2, 
  LogOut, 
  Trash2, 
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  X,
  Package,
  Monitor,
  Rocket,
  LayoutGrid,
  FolderCheck,
  Ban,
  Power,
  Layers,
  Pencil
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const SUPER_ADMIN_EMAIL = 'superalice@alice.camwood.com';

// Short plan names for the tenant list. Descriptions and availability come
// from the backend's plan catalogue.
const SERVICE_TIER_SHORT_LABELS = {
  full: 'Full plan',
  apps_patch_readonly: 'Read-only OS Patch',
  apps_only: 'Apps only',
};
const serviceTierShortLabel = (tier) =>
  SERVICE_TIER_SHORT_LABELS[tier] || SERVICE_TIER_SHORT_LABELS.full;

// Login request scopes
const loginRequest = {
  scopes: ['User.Read', 'openid', 'profile', 'email'],
};

function App() {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [accessToken, setAccessToken] = useState(null);
  const [userEmail, setUserEmail] = useState(null);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [tenantStats, setTenantStats] = useState({});
  const [tenantUsers, setTenantUsers] = useState([]);
  const [expandedTenant, setExpandedTenant] = useState(null);
  const [loadingTenantStats, setLoadingTenantStats] = useState({});
  
  // UI states
  const [error, setError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [suspendConfirm, setSuspendConfirm] = useState(null);
  const [suspending, setSuspending] = useState(false);
  const [notice, setNotice] = useState(null);

  // Plan change dialog
  const [tierCatalog, setTierCatalog] = useState([]);
  const [planTenant, setPlanTenant] = useState(null);
  const [planTarget, setPlanTarget] = useState('full');
  const [planSaving, setPlanSaving] = useState(false);

  // Get access token
  const getToken = useCallback(async () => {
    if (accounts.length > 0) {
      try {
        const response = await instance.acquireTokenSilent({
          ...loginRequest,
          account: accounts[0],
        });
        setAccessToken(response.accessToken);
        setUserEmail(accounts[0].username);
        return response.accessToken;
      } catch (error) {
        console.error('Token acquisition failed:', error);
        return null;
      }
    }
    return null;
  }, [instance, accounts]);

  // Check authorization
  useEffect(() => {
    const checkAuth = async () => {
      if (isAuthenticated && accounts.length > 0) {
        const email = accounts[0].username?.toLowerCase();
        setUserEmail(email);
        
        if (email === SUPER_ADMIN_EMAIL.toLowerCase()) {
          setIsAuthorized(true);
          await getToken();
        } else {
          setIsAuthorized(false);
        }
      }
      setLoading(false);
    };
    
    checkAuth();
  }, [isAuthenticated, accounts, getToken]);

  // API helper
  const apiCall = useCallback(async (method, endpoint, data = null) => {
    const token = accessToken || await getToken();
    if (!token) throw new Error('No access token');
    
    const config = {
      method,
      url: `${API_URL}${endpoint}`,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      data,
    };
    
    return axios(config);
  }, [accessToken, getToken]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await apiCall('get', '/api/tenant/admin/stats');
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
      setError('Failed to fetch statistics');
    }
  }, [apiCall]);

  // Fetch tenants
  const fetchTenants = useCallback(async () => {
    try {
      const response = await apiCall('get', '/api/tenant/admin/tenants');
      const tenantList = response.data.tenants || [];
      setTenants(tenantList);
      
      // Fetch stats for each tenant
      tenantList.forEach(tenant => {
        fetchTenantDetailedStats(tenant.tenant_id);
      });
    } catch (err) {
      console.error('Failed to fetch tenants:', err);
      setError('Failed to fetch tenants');
    }
  }, [apiCall]);

  // Fetch detailed stats for a tenant
  const fetchTenantDetailedStats = async (tenantId) => {
    setLoadingTenantStats(prev => ({ ...prev, [tenantId]: true }));
    try {
      const response = await apiCall('get', `/api/tenant/admin/tenants/${tenantId}/stats`);
      setTenantStats(prev => ({ ...prev, [tenantId]: response.data }));
    } catch (err) {
      console.error(`Failed to fetch stats for tenant ${tenantId}:`, err);
    } finally {
      setLoadingTenantStats(prev => ({ ...prev, [tenantId]: false }));
    }
  };

  // Fetch tenant users
  const fetchTenantUsers = async (tenantId) => {
    try {
      const response = await apiCall('get', `/api/tenant/admin/tenants/${tenantId}/users`);
      setTenantUsers(response.data || []);
    } catch (err) {
      console.error('Failed to fetch tenant users:', err);
    }
  };

  // Delete tenant
  const deleteTenant = async (tenantId) => {
    setDeleting(true);
    try {
      await apiCall('delete', `/api/tenant/admin/tenants/${tenantId}`);
      setDeleteConfirm(null);
      fetchTenants();
      fetchStats();
    } catch (err) {
      console.error('Failed to delete tenant:', err);
      setError('Failed to delete tenant');
    } finally {
      setDeleting(false);
    }
  };

  // Suspend tenant
  const suspendTenant = async (tenantId) => {
    setSuspending(true);
    try {
      await apiCall('post', `/api/tenant/admin/tenants/${tenantId}/suspend`);
      setSuspendConfirm(null);
      fetchTenants();
      fetchStats();
    } catch (err) {
      console.error('Failed to suspend tenant:', err);
      setError('Failed to suspend tenant');
    } finally {
      setSuspending(false);
    }
  };

  // Enable tenant
  const enableTenant = async (tenantId) => {
    setSuspending(true);
    try {
      await apiCall('post', `/api/tenant/admin/tenants/${tenantId}/enable`);
      fetchTenants();
      fetchStats();
    } catch (err) {
      console.error('Failed to enable tenant:', err);
      setError('Failed to enable tenant');
    } finally {
      setSuspending(false);
    }
  };

  // Plans offered by this server, with the app registration each uses
  const fetchTierCatalog = useCallback(async () => {
    try {
      const response = await apiCall('get', '/api/tenant/onboarding/service-tiers');
      setTierCatalog(Array.isArray(response.data?.tiers) ? response.data.tiers : []);
    } catch (err) {
      console.error('Failed to fetch plans:', err);
    }
  }, [apiCall]);

  const openPlanDialog = (tenant) => {
    setPlanTenant(tenant);
    setPlanTarget(tenant.pending_service_tier || tenant.service_tier || 'full');
  };

  // Change a tenant's plan. A plan on a different ALICE app registration stays
  // pending until one of the tenant's admins approves the new permissions.
  const changeTenantPlan = async () => {
    if (!planTenant) return;
    setPlanSaving(true);
    try {
      const response = await apiCall(
        'post',
        `/api/tenant/admin/tenants/${planTenant.tenant_id}/service-tier`,
        { service_tier: planTarget }
      );
      setNotice(response.data?.message || 'Plan updated');
      setPlanTenant(null);
      fetchTenants();
    } catch (err) {
      console.error('Failed to change plan:', err);
      setError(err.response?.data?.detail || 'Failed to change plan');
    } finally {
      setPlanSaving(false);
    }
  };

  const cancelPendingPlan = async () => {
    if (!planTenant) return;
    setPlanSaving(true);
    try {
      await apiCall('delete', `/api/tenant/admin/tenants/${planTenant.tenant_id}/service-tier`);
      setNotice(`Pending plan change for ${planTenant.display_name || planTenant.tenant_id} cancelled.`);
      setPlanTenant(null);
      fetchTenants();
    } catch (err) {
      console.error('Failed to cancel plan change:', err);
      setError(err.response?.data?.detail || 'Failed to cancel the plan change');
    } finally {
      setPlanSaving(false);
    }
  };

  // Load data when authorized
  useEffect(() => {
    if (isAuthorized && accessToken) {
      fetchStats();
      fetchTenants();
      fetchTierCatalog();
    }
  }, [isAuthorized, accessToken, fetchStats, fetchTenants, fetchTierCatalog]);

  // Toggle tenant expansion
  const toggleTenant = async (tenantId) => {
    if (expandedTenant === tenantId) {
      setExpandedTenant(null);
      setTenantUsers([]);
    } else {
      setExpandedTenant(tenantId);
      await fetchTenantUsers(tenantId);
    }
  };

  // Login handler
  const handleLogin = () => {
    instance.loginRedirect(loginRequest);
  };

  // Logout handler
  const handleLogout = () => {
    instance.logoutRedirect();
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return 'N/A';
    }
  };

  // Format relative time
  const formatRelativeTime = (dateStr) => {
    if (!dateStr) return 'Never';
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      return formatDate(dateStr);
    } catch {
      return 'N/A';
    }
  };

  // Calculate days since date
  const getDaysSince = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now - date;
      const diffDays = Math.floor(diffMs / 86400000);
      return diffDays;
    } catch {
      return '-';
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusMap = {
      active: { class: 'status-active', icon: CheckCircle, label: 'Active' },
      pending_setup: { class: 'status-pending', icon: Clock, label: 'Pending Setup' },
      pending_consent: { class: 'status-pending', icon: Clock, label: 'Pending Consent' },
      suspended: { class: 'status-suspended', icon: AlertTriangle, label: 'Suspended' },
    };
    
    const config = statusMap[status] || statusMap.pending_setup;
    const Icon = config.icon;
    
    return (
      <span className={`status-badge ${config.class}`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </span>
    );
  };

  // Stat pill component
  const StatPill = ({ icon: Icon, label, value, loading }) => (
    <div className="flex items-center gap-2 bg-white/5 px-3 py-1.5 rounded-lg" title={label}>
      <Icon className="w-3.5 h-3.5 text-white/50" />
      <span className="text-sm font-medium">
        {loading ? <RefreshCw className="w-3 h-3 animate-spin" /> : value ?? '-'}
      </span>
    </div>
  );

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4" />
          <p className="text-white/70">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="glass-card p-8 max-w-md w-full text-center">
          <Shield className="w-16 h-16 mx-auto mb-6 text-alice-cyan" />
          <h1 className="text-2xl font-bold mb-2">ALICE Admin Portal</h1>
          <p className="text-white/70 mb-6">Sign in with your administrator account to manage tenants.</p>
          <button
            onClick={handleLogin}
            className="w-full bg-alice-cyan hover:bg-alice-cyan/90 text-alice-purple font-semibold py-3 px-6 rounded-lg transition-colors"
          >
            Sign in with Microsoft
          </button>
        </div>
      </div>
    );
  }

  // Not authorized
  if (!isAuthorized) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="glass-card p-8 max-w-md w-full text-center">
          <AlertTriangle className="w-16 h-16 mx-auto mb-6 text-red-400" />
          <h1 className="text-2xl font-bold mb-2">Access Denied</h1>
          <p className="text-white/70 mb-4">
            This portal is restricted to super administrators only.
          </p>
          <p className="text-white/50 text-sm mb-6">
            Signed in as: {userEmail}
          </p>
          <button
            onClick={handleLogout}
            className="bg-white/10 hover:bg-white/20 text-white py-2 px-6 rounded-lg transition-colors"
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  // Main admin dashboard
  return (
    <div className="min-h-screen p-6">
      {/* Header */}
      <header className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <img 
            src="/alice-logo-royal.svg" 
            alt="ALICE" 
            className="h-12 w-auto"
          />
          <div>
            <h1 className="text-2xl font-bold">ALICE Admin</h1>
            <p className="text-white/60 text-sm">Tenant Management Portal</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-white/60 text-sm">{userEmail}</span>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 bg-white/10 hover:bg-white/20 py-2 px-4 rounded-lg transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </header>

      {/* Error message */}
      {error && (
        <div className="glass-card bg-red-500/20 border-red-500/30 p-4 mb-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            <span>{error}</span>
          </div>
          <button onClick={() => setError(null)}>
            <X className="w-5 h-5 text-white/60 hover:text-white" />
          </button>
        </div>
      )}

      {/* Success message */}
      {notice && (
        <div className="glass-card bg-green-500/20 border-green-500/30 p-4 mb-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-400" />
            <span>{notice}</span>
          </div>
          <button onClick={() => setNotice(null)}>
            <X className="w-5 h-5 text-white/60 hover:text-white" />
          </button>
        </div>
      )}

      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="glass-card p-6">
            <div className="flex items-center gap-3 mb-2">
              <Building2 className="w-5 h-5 text-alice-cyan" />
              <span className="text-white/60 text-sm">Total Tenants</span>
            </div>
            <p className="text-3xl font-bold">{stats.tenants?.total || 0}</p>
          </div>
          <div className="glass-card p-6">
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <span className="text-white/60 text-sm">Active Tenants</span>
            </div>
            <p className="text-3xl font-bold">{stats.tenants?.active || 0}</p>
          </div>
          <div className="glass-card p-6">
            <div className="flex items-center gap-3 mb-2">
              <Clock className="w-5 h-5 text-yellow-400" />
              <span className="text-white/60 text-sm">Pending Setup</span>
            </div>
            <p className="text-3xl font-bold">{stats.tenants?.pending || 0}</p>
          </div>
          <div className="glass-card p-6">
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-5 h-5 text-alice-cyan" />
              <span className="text-white/60 text-sm">Total Users</span>
            </div>
            <p className="text-3xl font-bold">{stats.users?.total || 0}</p>
          </div>
        </div>
      )}

      {/* Tenants list */}
      <div className="glass-card">
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <h2 className="text-xl font-semibold">All Tenants</h2>
          <button
            onClick={() => { fetchTenants(); fetchStats(); }}
            className="flex items-center gap-2 bg-white/10 hover:bg-white/20 py-2 px-4 rounded-lg transition-colors text-sm"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
        
        <div className="overflow-x-auto">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Tenant Name</th>
                <th>Status</th>
                <th>
                  <div className="flex items-center gap-1" title="ALICE plan">
                    <Layers className="w-3.5 h-3.5" /> Plan
                  </div>
                </th>
                <th>Age</th>
                <th>Users</th>
                <th>
                  <div className="flex items-center gap-1" title="Apps / Rationalised / Devices">
                    <LayoutGrid className="w-3.5 h-3.5" /> Stats
                  </div>
                </th>
                <th>Last Sync</th>
                <th>
                  <div className="flex items-center gap-1" title="Packaged / Deployed">
                    <Package className="w-3.5 h-3.5" /> Pkg/Dep
                  </div>
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((tenant) => {
                const tStats = tenantStats[tenant.tenant_id];
                const isLoadingStats = loadingTenantStats[tenant.tenant_id];
                
                return (
                  <React.Fragment key={tenant.tenant_id}>
                    <tr>
                      <td>
                        <div>
                          <p className="font-medium">{tenant.display_name}</p>
                          <p className="text-white/50 text-xs font-mono">{tenant.tenant_id}</p>
                        </div>
                      </td>
                      <td>{getStatusBadge(tenant.status)}</td>
                      <td>
                        {tenant.connection_type === 'sccm' ? (
                          <span
                            className="text-xs bg-sky-500/30 text-sky-100 px-2 py-1 rounded"
                            title="Onboarded from an SCCM export - no Intune connection or plan"
                          >
                            SCCM
                          </span>
                        ) : (
                          <div className="flex flex-col items-start gap-1">
                            <button
                              onClick={() => openPlanDialog(tenant)}
                              className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 py-1 px-2.5 rounded-lg transition-colors text-xs whitespace-nowrap"
                              title="Change plan"
                              data-testid={`tenant-plan-${tenant.tenant_id}`}
                            >
                              {serviceTierShortLabel(tenant.service_tier)}
                              <Pencil className="w-3 h-3 text-white/60" />
                            </button>
                            {tenant.pending_service_tier && (
                              <span
                                className="text-xs bg-amber-500/30 text-amber-100 px-2 py-0.5 rounded whitespace-nowrap"
                                title="Waiting for one of the tenant's admins to approve the new plan's permissions"
                                data-testid={`tenant-plan-pending-${tenant.tenant_id}`}
                              >
                                Pending: {serviceTierShortLabel(tenant.pending_service_tier)}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td>
                        <span className="text-white/70" title={formatDate(tenant.created_at)}>
                          {getDaysSince(tenant.created_at)} days
                        </span>
                      </td>
                      <td>{tenant.user_count || 0}</td>
                      <td>
                        <div className="flex items-center gap-2">
                          <StatPill 
                            icon={LayoutGrid} 
                            label="Apps" 
                            value={tStats?.apps_count} 
                            loading={isLoadingStats} 
                          />
                          <StatPill 
                            icon={FolderCheck} 
                            label="Rationalised" 
                            value={tStats?.rationalised_count} 
                            loading={isLoadingStats} 
                          />
                          <StatPill 
                            icon={Monitor} 
                            label="Devices" 
                            value={tStats?.devices_count} 
                            loading={isLoadingStats} 
                          />
                        </div>
                      </td>
                      <td className="text-white/70 text-sm">
                        {isLoadingStats ? (
                          <RefreshCw className="w-3 h-3 animate-spin" />
                        ) : (
                          formatRelativeTime(tStats?.last_sync)
                        )}
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <StatPill 
                            icon={Package} 
                            label="Packaged" 
                            value={tStats?.packages_count} 
                            loading={isLoadingStats} 
                          />
                          <StatPill 
                            icon={Rocket} 
                            label="Deployed" 
                            value={tStats?.deployed_count} 
                            loading={isLoadingStats} 
                          />
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => toggleTenant(tenant.tenant_id)}
                            className="flex items-center gap-1 bg-white/10 hover:bg-white/20 py-1.5 px-3 rounded-lg transition-colors text-sm"
                          >
                            <Users className="w-4 h-4" />
                            Users
                            {expandedTenant === tenant.tenant_id ? (
                              <ChevronUp className="w-4 h-4" />
                            ) : (
                              <ChevronDown className="w-4 h-4" />
                            )}
                          </button>
                          {tenant.status === 'suspended' ? (
                            <button
                              onClick={() => enableTenant(tenant.tenant_id)}
                              disabled={suspending}
                              className="flex items-center gap-1 bg-green-500/20 hover:bg-green-500/30 text-green-300 py-1.5 px-3 rounded-lg transition-colors text-sm"
                            >
                              <Power className="w-4 h-4" />
                              Enable
                            </button>
                          ) : (
                            <button
                              onClick={() => setSuspendConfirm(tenant)}
                              className="flex items-center gap-1 bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-300 py-1.5 px-3 rounded-lg transition-colors text-sm"
                            >
                              <Ban className="w-4 h-4" />
                              Disable
                            </button>
                          )}
                          <button
                            onClick={() => setDeleteConfirm(tenant)}
                            className="flex items-center gap-1 bg-red-500/20 hover:bg-red-500/30 text-red-300 py-1.5 px-3 rounded-lg transition-colors text-sm"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                    
                    {/* Expanded user list */}
                    {expandedTenant === tenant.tenant_id && (
                      <tr>
                        <td colSpan="9" className="bg-white/5 p-0">
                          <div className="p-4">
                            <h4 className="text-sm font-semibold mb-3 text-white/70">Tenant Users</h4>
                            {tenantUsers.length > 0 ? (
                              <div className="grid gap-2">
                                {tenantUsers.map((user, idx) => (
                                  <div key={idx} className="flex items-center justify-between bg-white/5 p-3 rounded-lg">
                                    <div>
                                      <p className="font-medium">{user.display_name || user.email}</p>
                                      <p className="text-white/50 text-sm">{user.email}</p>
                                    </div>
                                    <div className="flex items-center gap-4">
                                      <span className="text-xs bg-white/10 px-2 py-1 rounded">
                                        {user.role_display_name || user.role}
                                      </span>
                                      <span className="text-white/50 text-xs">
                                        Last login: {formatDate(user.last_login)}
                                      </span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-white/50 text-sm">No users found</p>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              
              {tenants.length === 0 && (
                <tr>
                  <td colSpan="9" className="text-center py-8 text-white/50">
                    No tenants found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="glass-card p-6 max-w-md w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <h3 className="text-xl font-semibold">Delete Tenant</h3>
            </div>
            <p className="text-white/70 mb-2">
              Are you sure you want to delete this tenant?
            </p>
            <div className="bg-white/5 p-3 rounded-lg mb-4">
              <p className="font-medium">{deleteConfirm.display_name}</p>
              <p className="text-white/50 text-sm font-mono">{deleteConfirm.tenant_id}</p>
            </div>
            <p className="text-red-300 text-sm mb-6">
              This action is irreversible. All tenant data, users, and packages will be permanently deleted.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className="flex-1 bg-white/10 hover:bg-white/20 py-2 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteTenant(deleteConfirm.tenant_id)}
                disabled={deleting}
                className="flex-1 bg-red-500 hover:bg-red-600 py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {deleting ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                {deleting ? 'Deleting...' : 'Delete Tenant'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Plan change modal */}
      {planTenant && (() => {
        const currentTier = planTenant.service_tier || 'full';
        const currentRegistration = tierCatalog.find((t) => t.tier === currentTier)?.registration;
        const target = tierCatalog.find((t) => t.tier === planTarget);
        const unchanged = planTarget === currentTier;
        const needsConsent = !unchanged && target && currentRegistration && target.registration !== currentRegistration;
        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="glass-card p-6 max-w-lg w-full" data-testid="plan-dialog">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-alice-cyan/20 flex items-center justify-center">
                  <Layers className="w-5 h-5 text-alice-cyan" />
                </div>
                <h3 className="text-xl font-semibold">Change Plan</h3>
              </div>
              <div className="bg-white/5 p-3 rounded-lg mb-4">
                <p className="font-medium">{planTenant.display_name}</p>
                <p className="text-white/50 text-sm font-mono">{planTenant.tenant_id}</p>
                <p className="text-white/70 text-sm mt-2">
                  Current plan: <span className="font-medium text-white">{serviceTierShortLabel(currentTier)}</span>
                </p>
              </div>

              {planTenant.pending_service_tier && (
                <div className="bg-amber-500/20 border border-amber-500/30 p-3 rounded-lg mb-4 text-sm">
                  <p className="mb-2">
                    Waiting for a tenant admin to approve <span className="font-medium">{serviceTierShortLabel(planTenant.pending_service_tier)}</span> in
                    Settings &gt; Integrations &gt; ALICE Plan.
                  </p>
                  <button
                    onClick={cancelPendingPlan}
                    disabled={planSaving}
                    className="bg-white/10 hover:bg-white/20 py-1 px-3 rounded-lg transition-colors text-xs"
                    data-testid="plan-cancel-pending"
                  >
                    Cancel pending change
                  </button>
                </div>
              )}

              <div className="space-y-2 mb-4">
                {tierCatalog.length === 0 && (
                  <p className="text-white/50 text-sm">Loading plans...</p>
                )}
                {tierCatalog.map((tier) => {
                  const unavailable = !tier.available && tier.tier !== currentTier;
                  return (
                    <label
                      key={tier.tier}
                      className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                        planTarget === tier.tier ? 'border-alice-cyan bg-white/10' : 'border-white/10 bg-white/5'
                      } ${unavailable ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-white/10'}`}
                    >
                      <input
                        type="radio"
                        name="service-tier"
                        className="mt-1"
                        value={tier.tier}
                        checked={planTarget === tier.tier}
                        disabled={unavailable || planSaving}
                        onChange={() => setPlanTarget(tier.tier)}
                      />
                      <div>
                        <p className="font-medium text-sm">
                          {tier.label}
                          {tier.tier === currentTier && <span className="text-white/50 font-normal"> (current)</span>}
                        </p>
                        <p className="text-white/60 text-xs mt-0.5">{tier.description}</p>
                        {unavailable && (
                          <p className="text-red-300 text-xs mt-1">
                            The app registration for this plan isn't configured on this server.
                          </p>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>

              {!unchanged && (
                <p className={`text-sm mb-6 ${needsConsent ? 'text-amber-200' : 'text-white/70'}`}>
                  {needsConsent
                    ? 'This plan uses a different ALICE app registration. The tenant keeps its current plan until one of its admins approves the new permissions in Settings > Integrations > ALICE Plan.'
                    : 'This plan uses the same ALICE app registration, so the change applies straight away.'}
                </p>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => setPlanTenant(null)}
                  disabled={planSaving}
                  className="flex-1 bg-white/10 hover:bg-white/20 py-2 px-4 rounded-lg transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={changeTenantPlan}
                  disabled={planSaving || unchanged || !target}
                  className="flex-1 bg-alice-cyan hover:bg-alice-cyan/90 text-alice-purple font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="plan-save"
                >
                  {planSaving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Layers className="w-4 h-4" />}
                  {needsConsent ? 'Request Plan Change' : 'Change Plan'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Suspend confirmation modal */}
      {suspendConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="glass-card p-6 max-w-md w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                <Ban className="w-5 h-5 text-yellow-400" />
              </div>
              <h3 className="text-xl font-semibold">Disable Tenant</h3>
            </div>
            <p className="text-white/70 mb-2">
              Are you sure you want to disable this tenant?
            </p>
            <div className="bg-white/5 p-3 rounded-lg mb-4">
              <p className="font-medium">{suspendConfirm.display_name}</p>
              <p className="text-white/50 text-sm font-mono">{suspendConfirm.tenant_id}</p>
            </div>
            <p className="text-yellow-300 text-sm mb-6">
              Disabled tenants will not be able to access the application. You can re-enable them at any time.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setSuspendConfirm(null)}
                disabled={suspending}
                className="flex-1 bg-white/10 hover:bg-white/20 py-2 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => suspendTenant(suspendConfirm.tenant_id)}
                disabled={suspending}
                className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {suspending ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Ban className="w-4 h-4" />
                )}
                {suspending ? 'Disabling...' : 'Disable Tenant'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
